import { EditedTask, NewTaskTag } from "@/types";
import { formatDueDate } from "@/utils/utils";
import { set } from "date-fns";
import { Dispatch, SetStateAction, useCallback } from "react";

const useFieldHandlers = (
  editedTask: EditedTask | null,
  setEditedTask: Dispatch<SetStateAction<EditedTask | null>>
) => {
  // Updates the edited task with a new assignee
  const handleFieldAssignedTo = useCallback(
    (value: number) => {
      if (isNaN(value)) {
        return;
      }
      if (editedTask) {
        setEditedTask((prev) => {
          if (!prev) return null;
          return { ...prev, assigned_to: value };
        });
      }
    },
    [editedTask, setEditedTask]
  );

  // Updates the edited task with a new field value
  const handleEditField = useCallback(
    (field: keyof EditedTask, value: string) => {
      if (editedTask) {
        setEditedTask((prev) => {
          return {
            ...prev,
            [field]: value,
          } as EditedTask;
        });
      }
    },
    [editedTask, setEditedTask]
  );

  // Updates the edited task with a new due date
  const handleFieldDueDate = useCallback(
    (value: Date | undefined | null) => {
      if (!value) {
        setEditedTask((prev) => {
          if (!prev) return null;
          return { ...prev, due_date: null };
        });
        return;
      }

      const date = set(new Date(), {
        year: value.getFullYear(),
        month: value.getMonth(),
        date: value.getDate(),
      });
      const formatted = formatDueDate(date);
      setEditedTask((prev) => {
        if (!prev) return null;
        // setIsCalendarOpen(false);
        return { ...prev, due_date: formatted };
      });
    },
    [setEditedTask]
  );

  // Updates the edited task with a new tag
  const handleFieldTags = useCallback(
    (newTag: NewTaskTag) => {
      setEditedTask((prev) => {
        if (!prev) return null;
        const updatedTags = prev.tags?.some((t) => t.tag === newTag.tag)
          ? prev.tags.filter((t) => t.tag !== newTag.tag)
          : [...(prev.tags || []), newTag];
        return { ...prev, tags: updatedTags };
      });
    },
    [setEditedTask]
  );

  const handleFieldCollaborators = useCallback(
    (id: number) => {
      // add or remove collaborator from the array of collaborators
      if (editedTask) {
        setEditedTask((prev) => {
          if (!prev) return null;

          const updatedCollaborators = prev.collaborators?.includes(id)
            ? prev.collaborators.filter((c) => c !== id)
            : [...(prev.collaborators || []), id];
          return { ...prev, collaborators: updatedCollaborators };
        });
      }
    },
    [editedTask, setEditedTask]
  );

  return {
    handleFieldAssignedTo,
    handleEditField,
    handleFieldDueDate,
    handleFieldTags,
    handleFieldCollaborators,
  };
};

export default useFieldHandlers;
